import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme, Theme } from '../contexts/ThemeContext';
import CloseIcon from './icons/CloseIcon';
import iconAndroid from '../assets/icon-android.png';
import { generateTransferCode, buildTransferDeepLink, isChartEmpty } from '../utils/transferCodeUtils';
import { ChordCakeState } from '../types';

/**
 * App Install Banner Component
 * 
 * Shows platform-specific app install prompts on mobile devices:
 * - iOS: Native Smart Banner (via static meta tag in index.html)
 * - Android: Custom banner with theme support
 * 
 * Features:
 * - Shows on all mobile devices (phones and tablets, including iPad)
 * - iOS: Native Smart Banner only (no custom banner needed)
 * - Android: Custom banner (dismissible with localStorage persistence)
 * - Links to respective app stores
 * - Transfer code generation for seamless web-to-app state transfer
 * 
 * Transfer Code Flow:
 * 1. Detects if user has active chart content
 * 2. Generates transfer code via Buffet API
 * 3. Updates deep link to include transfer code
 * 4. Native app retrieves chart state using transfer code
 * 
 * Note: iOS Smart Banner requires static meta tag in index.html.
 * Safari only parses the apple-itunes-app meta tag on initial page load.
 * Dynamic injection via JavaScript does not work.
 */

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.chordbaker';

interface AppInstallBannerProps {
  chartData?: ChordCakeState | null;
}

const AppInstallBanner: React.FC<AppInstallBannerProps> = ({ chartData }) => {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const [showBanner, setShowBanner] = useState(false);
  const [transferCode, setTransferCode] = useState<string | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  // Generate transfer code when chart data changes
  useEffect(() => {
    const generateCode = async () => {
      // Only generate if we have chart data and it's not empty
      if (!chartData || isChartEmpty(chartData)) {
        setTransferCode(null);
        return;
      }

      // Don't generate if already generating
      if (isGeneratingCode) {
        return;
      }

      try {
        setIsGeneratingCode(true);
        
        const response = await generateTransferCode(chartData);
        setTransferCode(response.transferCode);

        // Update the current URL to include the transfer code
        // This makes the iOS Smart Banner open the app with the transfer code
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('transfer', response.transferCode);
        
        // Update URL without reloading the page
        window.history.replaceState({}, '', currentUrl.toString());
      } catch (error) {
        console.error('Failed to generate transfer code:', error);
        // Don't block the banner, just log the error
        setTransferCode(null);
      } finally {
        setIsGeneratingCode(false);
      }
    };

    generateCode();
  }, [chartData]);

  useEffect(() => {
    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = /Mobile/i.test(navigator.userAgent);
    const isTablet = /iPad/.test(navigator.userAgent) || 
                     (isAndroid && !/Mobile/.test(navigator.userAgent));

    // iOS: Native Smart Banner is handled by static meta tag in index.html
    // No custom banner needed for iOS
    if (isIOS) {
      return;
    }

    // Android: Show custom banner (always show, no dismiss persistence)
    if (isAndroid && (isMobile || isTablet)) {
      setShowBanner(true);
    }
  }, [searchParams, transferCode]);

  const handleDismiss = () => {
    setShowBanner(false);
    // Note: Not persisting dismiss state anymore - banner will show again on next visit
  };

  const handleInstall = () => {
    const deepLink = transferCode 
      ? buildTransferDeepLink(transferCode)
      : 'chordbaker://';
    
    // Detect if Chrome browser
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    
    if (isChrome) {
      // Chrome: Use intent URL (works best in Chrome)
      // Format: intent://[path]#Intent;scheme=[scheme];package=[package];S.browser_fallback_url=[url];end
      const path = deepLink.replace('chordbaker://', '');
      const intentUrl = `intent://${path}#Intent;scheme=chordbaker;package=com.chordbaker;S.browser_fallback_url=${encodeURIComponent(GOOGLE_PLAY_URL)};end`;
      
      window.location.href = intentUrl;
    } else {
      // Other browsers: Try custom scheme with timeout fallback
      let appOpened = false;
      let timeout: NodeJS.Timeout;
      
      // Set up visibility change listener to detect if app opened
      const handleVisibilityChange = () => {
        if (document.hidden) {
          appOpened = true;
          clearTimeout(timeout);
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Try to open the app with custom scheme
      window.location.href = deepLink;
      
      // If app doesn't open within 2 seconds, redirect to Play Store
      timeout = setTimeout(() => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        
        if (!appOpened) {
          window.location.href = GOOGLE_PLAY_URL;
        }
      }, 2000);
    }
  };

  // Only render custom banner if needed
  if (!showBanner) {
    return null;
  }

  const styles = createStyles(theme);

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.iconContainer}>
          <img 
            src={iconAndroid} 
            alt="ChordBaker" 
            style={styles.icon}
          />
        </div>
        
        <div style={styles.textContainer}>
          <div style={styles.title}>ChordBaker</div>
          <div style={styles.subtitle}>Get the app for a better experience</div>
        </div>

        <button
          onClick={handleInstall}
          style={styles.installButton}
          aria-label="Open ChordBaker app"
        >
          Open
        </button>

        <button
          onClick={handleDismiss}
          style={styles.closeButton}
          aria-label="Dismiss banner"
        >
          <CloseIcon size={16} color="currentColor" />
        </button>
      </div>
    </div>
  );
};

const createStyles = (theme: Theme) => ({
  container: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    backgroundColor: theme.colors.surface,
    borderBottom: `1px solid ${theme.colors.border}`,
    boxShadow: theme.shadows.card,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
    gap: `${theme.spacing.md}px`,
    maxWidth: '100%',
  },
  iconContainer: {
    flexShrink: 0,
  },
  icon: {
    width: 48,
    height: 48,
    borderRadius: theme.radii.xl, // Rounded corners for app icon look
    display: 'block',
  },
  textContainer: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: `${theme.typography.sizes.lg}px`,
    fontWeight: theme.typography.weights.semiBold,
    color: theme.colors.text,
    lineHeight: theme.typography.lineHeights.tight,
  },
  subtitle: {
    fontSize: `${theme.typography.sizes.sm}px`,
    color: theme.colors.textSecondary,
    lineHeight: theme.typography.lineHeights.normal,
    marginTop: `${theme.spacing.xxs}px`,
  },
  installButton: {
    flexShrink: 0,
    padding: `${theme.spacing.sm}px ${theme.spacing.xl}px`,
    backgroundColor: theme.colors.buttonPrimary,
    color: theme.colors.textInverse,
    border: 'none',
    borderRadius: `${theme.radii.md}px`,
    fontSize: `${theme.typography.sizes.md}px`,
    fontWeight: theme.typography.weights.semiBold,
    cursor: 'pointer',
    transition: `opacity ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
  closeButton: {
    flexShrink: 0,
    padding: `${theme.spacing.xs}px`,
    backgroundColor: 'transparent',
    color: theme.colors.textTertiary,
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: `color ${theme.motion.duration.fast}ms ${theme.motion.easing.standard}`,
  },
});

export default AppInstallBanner;
